import { useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { collection } from 'firebase/firestore';

import ActionButton from '@src/components/Buttons/ActionButton';
import AuditLogViewer from '@src/components/AuditLogViewer';
import { useSecondaryPanel } from '@src/components/SecondaryPanel/SecondaryPanel';
import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { IUserJob } from '@src/models/interfaces/user-form.interface';
import {
  filterValuesToJobsListFilters,
  INIT_JOBS_LIST_FILTERS,
} from '@src/packages/jobs/jobsListFilterConfig';
import {
  ALLOWED_ROLES,
  useCollectionWithSubcollectionsPaginated,
  usePaginationWithFirestore,
  useSortableTable,
  useToggle,
} from '@src/packages/utils';
import UserJobService from '@src/services/UserJobService';
import safeExecute from '@src/util/safeExecute';

const PAGE_SIZE = 10;

export const useJobs = () => {
  const intl = useIntl();
  const orgId = useSelector((state: CommonReduxState) => state.organizations.organization.id);
  const userJobIdRef = useRef<string | null>(null);
  const [positionFormVisible, positionFormVisibleToggle] = useToggle();
  const [loading, loadingToggle] = useToggle();
  const [refreshTrigger, setRefreshTrigger] = useState(1);

  const {
    values: filters,
    debouncedValues,
    setValue: setFilters,
    toggle,
    setValues,
    isOpen,
  } = useSecondaryPanel({ debounceDelay: 500 });

  const listFilters = useMemo(
    () => filterValuesToJobsListFilters(debouncedValues),
    [debouncedValues],
  );

  const fsOptions = {
    loadSubcollectionsLazy: false,
    realtime: false,
  };

  const jobCollectionRef = collection(
    db,
    FSCollections.ORGANIZATIONS,
    orgId,
    FSCollections.USER_JOBS,
  );
  const firestoreResult = useCollectionWithSubcollectionsPaginated(
    jobCollectionRef,
    null,
    PAGE_SIZE,
    [orgId, refreshTrigger],
    fsOptions,
  );

  const { data: jobs = [], loading: jobsLoading, error } = firestoreResult;

  const jobsFiltered = useMemo(() => {
    const search = listFilters.name.toLowerCase();
    if (!search) return jobs as IUserJob[];
    return (jobs as IUserJob[]).filter(job => job.name.toLowerCase().includes(search));
  }, [jobs, listFilters.name]);

  const { sortedData, createSortableColumn } = useSortableTable(jobsFiltered);

  const paginationState = usePaginationWithFirestore(
    sortedData,
    {
      gotNewData: firestoreResult.gotNewData,
      loading: firestoreResult.loading,
      next: firestoreResult.next,
    },
    PAGE_SIZE,
    { jobName: listFilters.name, refreshTrigger },
  );

  const onEditJob = (data: IUserJob) => {
    userJobIdRef.current = data.id;
    positionFormVisibleToggle();
  };

  const onDeleteJob = (id: string) => {
    const userJob = new UserJobService();
    safeExecute(async () => {
      Modal.confirm({
        title: intl.formatMessage({ id: 'general.confirmMessage' }),
        icon: <ExclamationCircleOutlined />,
        okText: intl.formatMessage({ id: 'general.yes' }),
        cancelText: intl.formatMessage({ id: 'general.no' }),
        onOk: async () => {
          loadingToggle();
          await userJob.delete({ organizationId: orgId, userJobId: id });
          loadingToggle();
          setRefreshTrigger(prev => prev + 1);
        },
      });
    });
  };

  const baseColumns = [
    {
      key: 'name',
      title: intl.formatMessage({ id: 'position.job.column.name' }),
      dataIndex: 'name',
      align: 'left' as const,
      render: (job: IUserJob) => job.name,
      sortable: 'name',
    },
    {
      key: 'actions',
      title: '',
      dataIndex: 'actions',
      align: 'center' as const,
      width: 120,
      render: (job: IUserJob) => (
        <ActionButton.Container
          buttons={[
            <ActionButton
              icon="edit"
              key="edit"
              onClick={() => onEditJob(job)}
              tooltipText={intl.formatMessage({ id: 'button.edit' })}
              allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.JOBS.UPDATE}
            />,
            <ActionButton
              icon="delete"
              color="danger"
              key="delete"
              onClick={() => onDeleteJob(job.id)}
              tooltipText={intl.formatMessage({ id: 'button.delete' })}
              allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.JOBS.DELETE}
            />,
            <AuditLogViewer
              key="history"
              entityType={FSCollections.USER_JOBS}
              entityId={job.id}
              recordLabel={job.name || job.id}
              triggerButton={
                <ActionButton
                  icon="history"
                  tooltipText={intl.formatMessage({ id: 'general.history.label' })}
                />
              }
            />,
          ]}
        />
      ),
    },
  ];

  const columns = baseColumns.map(createSortableColumn);

  useEffect(() => {
    if (!positionFormVisible) {
      userJobIdRef.current = null;
    }
  }, [positionFormVisible]);

  useEffect(() => {
    setValues(INIT_JOBS_LIST_FILTERS);
  }, [setValues]);

  return {
    columns,
    paginationState,
    onEditJob,
    onDeleteJob,
    positionFormVisible,
    positionFormVisibleToggle,
    loading,
    loadingToggle,
    jobsLoading,
    error,
    jobs: jobs as IUserJob[],
    userJobIdRef,
    refreshTrigger,
    setRefreshTrigger,
    filters,
    setFilters,
    toggle,
    isOpen,
  };
};
